/**
 * @ngdoc object
 * @name eventix.common.services.Currencies
 * @description
 * Array containing available currencies and a storage interface
 * for saving user preferred currency
 */
/**
 * @ngdoc object
 * @name eventix.common.services.CurrenciesProvider
 * @description
 * Array containing available currencies and a storage interface
 * for saving user preferred currency
 */
export default angular.module('eventix.common.services.currencies', [])
    .provider('Currencies', function() {
        const provider = this;
        var storageKey = 'evt.currency';
        var defaultCurrency = DEFAULT_CURRENCY;
        var allowedCurrencies = ALLOWED_CURRENCIES;
        var rates = {};
        var fee = 0;
        var refreshRate = 0;

        /**
         * @ngdoc method
         * @name eventix.common.services.CurrenciesProvider#setRefreshRate
         * @methodOf eventix.common.services.CurrenciesProvider
         * @description
         * Enable automatic refresh after X seconds (disabled by default)
         *
         * @param {Number} seconds The interval between refreshes in seconds (0 disables refreshing)
         */
        provider.setRefreshRate = seconds => {
            refreshRate = parseInt(seconds, 10) * 1000;
        };

        /**
         * @ngdoc method
         * @name eventix.common.services.CurrenciesProvider#setDefaultCurrency
         * @methodOf eventix.common.services.CurrenciesProvider
         * @description
         * Set the default currency for new users
         *
         * @param {String} code The currency code (e.g.: EUR, USD, etc)
         */
        provider.setDefaultCurrency = code => {
            defaultCurrency = code;
        };

        /**
         * @ngdoc method
         * @name eventix.common.services.CurrenciesProvider#setStorageKey
         * @methodOf eventix.common.services.CurrenciesProvider
         * @description
         * Use another key for the LocalStorage service
         *
         * @example
         * <pre>
         * CurrencyStoreProvider.setStorageKey('evt.currency');
         * </pre>
         *
         * @param {String} key New key name
         */
        provider.setStorageKey = key => {
            storageKey = key;
        };

        provider.$get = /*@ngInject*/function($window, $interval, $http, $rootScope, store, $log) {
            var selected = store.get(storageKey) || defaultCurrency;
            if (refreshRate)
                $interval(updateRates, refreshRate);
            updateRates();

            let allCurrencies = [
                { label: '(€) Euro', code: 'EUR' },
                { label: '($) US Dollar', code: 'USD' },
                { label: '(£) Pound', code: 'GBP' },
                { label: '($) Mexico Peso', code: 'MXN' },
                { label: '(Q) Guatemalan Quetzal', code: 'GTQ' },
                { label: '(Ft) Hungarian Forint', code: 'HUF' },
                { label: '(Fr.) Swiss Francs', code: 'CHF' },
                { label: '(AU $) Australian Dollar', code: 'AUD' },
                { label: '(S$) Singapore Dollars', code: 'SGD' },
                { label: '(RM) Malaysian Ringgits', code: 'MYR' },
                { label: '(₱) Philippine Pesos', code: 'PHP' },
                { label: '(JP¥) Japanese yen', code: 'JPY' },
                { label: '(NT$) Taiwan dollar', code: 'TWD' },
                { label: '(฿) Thai baht', code: 'THB' },
                { label: '(kr.) Danish krone', code: 'DKK' },
                { label: '(HK$) Hong Kong dollar', code: 'HKD' },
                { label: '($) Colombian peso', code: 'COP' },
                { label: '(kr) Swedish krona', code: 'SEK' },
                { label: '(zł) Polish złoty', code: 'PLN' },
                { label: '(₹) Indian Rupee', code:'INR'},
                { label: '($) Canadian Dollar', code:'CAD'},
                { label: '(¥) Chinese Yuan Renminbi', code:'CNY'},
                { label: '($) New Zealand Dollar', code:'NZD'},
                { label: '(R) South African Rand', code:'ZAR'},
                { label: '(Rp) Indonesian Rupiah', code:'IDR'},
                { label: '(﷼) Saudi Arabian Riyal', code:'SAR'},
                { label: '(R$) Brazilian Real', code:'BRL'},
                { label: '(TL) Turkish Lira', code:'TRY'},
                { label: '(₩) South Korean Won', code:'KRW'},
                { label: '(£) Egyptian Pound', code:'EGP'},
                { label: '(kr) Norwegian Krone', code:'NOK'},
                { label: '(₽) Russian Ruble', code:'RUB'},
                { label: '(₨) Pakistani Rupee', code:'PKR'},
                { label: '(₪) Israeli Shekel', code:'ILS'},
                { label: '(﷼) Qatari Riyal', code:'QAR'},
                { label: '(﷼) Omani Rial', code:'OMR'},
                { label: '($) Chilean Peso', code:'CLP'},
                { label: '($) Argentine Peso', code:'ARS'},
                { label: '(Kč) Czech Koruna', code:'CZK'},
                { label: '(₫) Vietnamese Dong', code:'VND'},
                { label: '(د.م.) Moroccan Dirham', code:'MAD'},
                { label: '(BGN) Bulgarian lev', code:'BGN'}
            ];

            // Filter for current WL
            const Currencies = _.filter(allCurrencies, c => allowedCurrencies.includes(c.code));

            _.forEach(Currencies, c => {
                c.icon = 'fa fa-' + c.code.toLowerCase();
            });
            Object.defineProperties(Currencies, {
                /**
                 * @ngdoc property
                 * @name eventix.common.services.Currencies#selected
                 * @propertyOf eventix.common.services.Currencies
                 * @description
                 * Currency setting
                 */
                selected: {
                    get: () => selected,
                    set: c => selected = c,
                    enumerable: false
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.Currencies#use
                 * @methodOf eventix.common.services.Currencies
                 * @description
                 * Save currency setting
                 *
                 * @example
                 * <pre>
                 * Currencies.use('EUR');
                 * </pre>
                 *
                 * @param {String} currency Code identifying the currency (EUR, USD, etc)
                 */
                use: {
                    value: function(currency) {
                        this.selected = currency;
                        store.set(storageKey, currency);
                    },
                    enumerable: false
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.Currencies#get
                 * @methodOf eventix.common.services.Currencies
                 * @description
                 * Restore currency setting
                 *
                 * @returns {String} Code identifying the currency (EUR, USD, etc)
                 */
                get: {
                    value: () => {
                        return this.selected;
                    },
                    enumerable: false
                },
                /**
                 * @ngdoc method
                 * @name eventix.common.services.Currencies#exchange
                 * @methodOf eventix.common.services.Currencies
                 * @description
                 * Service that can convert currencies and updates the exchange rates
                 *
                 * @param {Number} amount The amount of money in the source currency
                 * @param {String} from The code for the money's source currency
                 * @param {String} to The code the money's target currency, defaults to selected currency
                 * @param {Boolean} applyFee Apply the fee for converting currencies, default true
                 * @returns {Number} The amount of money in the target currency
                 *
                 * @example
                 * <pre>
                 * //$10 => €9.5
                 * CurrencyExchange(10, 'USD', 'EUR');
                 * </pre>
                 */
                exchange: {
                    value: (amount, from, to = null, applyFee = true) => {
                        if (from === to) return amount;
                        if (!to) to = this.selected;
                        rates[to] = rates[to] || 1;
                        rates[from] = rates[from] || 1;
                        if (from !== defaultCurrency)
                            amount = (1 / rates[from]) * amount;
                        if (applyFee)
                            amount += amount * fee;
                        return amount * rates[to];
                    },
                    enumerable: false
                },

                parseCurrency: {
                    value: (value, currency) => {
                        let signCount = 0;

                        if (typeof value === 'string') {
                            // Replace illegal characters (anything that is not a digit, comma, period or dash)
                            value = value.replace(/([^0-9.,-])/g, '');

                            // Replace decimal zero replacement with actual zeros
                            value = value.replace(/(?:([.,])-)/g, '$100');

                            // Replace and count minus signs (first and/or last character)
                            value = value.replace(/(^-|-$)/gm, function(match, p1, offset, string) {
                                signCount++;
                                return '';
                            });

                            // Replace all dashes (All remaining dashes are illegal characters) and a trailing decimal delimeter
                            value = value.replace(/([,.]$|-)/gm, '');

                            // Replace the decimal separator with a pipe (temporary character)
                            value = value.replace(/([,.])([0-9]{1,2})$/gm, '|$2');

                            // Replace all thousand separators ([.,])
                            value = value.replace(/[,.]/g, '');

                            // Change the temporary pipe character to a universal decimal separator (.)
                            value = value.replace(/\|/g, '.');
                        }

                        value = parseFloat(value);

                        if (isNaN(value))
                            return undefined;

                        switch (currency) {
                            //@TODO add extra cases for currencies that don't have a 1/100 denomination
                            default:
                                value = Math.round(value * 100);
                        }

                        if (signCount)
                            value = (-1) * value;

                        return value;
                    },
                    enumerable: false
                }
            });

            return Currencies;

            function updateRates() {
                let url = SHOP_API_ROOT + '/exchangeRates/' + defaultCurrency;
                if(typeof USE_SHOP_CACHED !== 'undefined' && USE_SHOP_CACHED == 1){
                    url = 'https://ev3b.s3-eu-west-1.amazonaws.com/peak/' + defaultCurrency + '.json';
                }
                $http.get(url).then(response => {
                    rates = response.data.rates;
                    fee = response.data.fee;
                    $rootScope.$broadcast('CurrencyExchangeRatesUpdated');
                }, response => {
                    $log.error('Error fetching exchange rates', response.statusText);
                });
            }
        };
    }).name;
